import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from 'reactstrap';
import SuperAdminService from '../../service/super-admin.service';
import { useNotification } from '../../contexts/NotificationContext';
import { formatPlanPrice } from '../../utils/planWhatsapp';

function moneyToCents(value) {
  const cleaned = String(value || '')
    .replace(/[^\d,.-]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');
  const num = Number(cleaned);
  if (Number.isNaN(num) || num < 0) return 0;
  return Math.round(num * 100);
}

function centsToMoneyInput(cents) {
  return ((cents || 0) / 100).toFixed(2).replace('.', ',');
}

function featureMapFromPlan(plan, catalog) {
  const byKey = new Map((plan?.features || []).map((f) => [f.featureKey, f]));
  return catalog.map((item) => {
    const existing = byKey.get(item.key);
    return {
      featureKey: item.key,
      enabled: existing ? !!existing.enabled : false,
      limitValue:
        existing?.limitValue != null
          ? existing.limitValue
          : item.acceptsLimit
            ? item.defaultLimit
            : null,
      label: item.label,
      description: item.description,
      acceptsLimit: item.acceptsLimit,
      defaultLimit: item.defaultLimit,
    };
  });
}

function SuperAdminPlansPage() {
  const navigate = useNavigate();
  const { notify } = useNotification();
  const [plans, setPlans] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editOpen, setEditOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState({
    name: '',
    description: '',
    priceInput: '99,90',
    badgeColor: '#3b82f6',
    highlight: false,
    active: true,
    sortOrder: 0,
    aiProvider: 'DEEPSEEK',
    aiModelId: 'deepseek-v4-flash',
    hasAiApiKey: false,
  });
  const [aiApiKeyInput, setAiApiKeyInput] = useState('');
  const [aiModels, setAiModels] = useState([]);
  const [features, setFeatures] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [plansData, catalogData, modelsData] = await Promise.all([
        SuperAdminService.listPlans(true),
        SuperAdminService.getFeatureCatalog(),
        SuperAdminService.listAiModels(true),
      ]);
      setPlans(Array.isArray(plansData) ? plansData : []);
      setCatalog(Array.isArray(catalogData) ? catalogData : []);
      setAiModels(Array.isArray(modelsData) ? modelsData : []);
    } catch (err) {
      notify('tr', 'danger', 'Erro ao carregar planos', 'icon-alert-circle-exc');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setCreating(true);
    setEditingPlan(null);
    setAiApiKeyInput('');
    setForm({
      name: '',
      description: '',
      priceInput: '99,90',
      badgeColor: '#22c55e',
      highlight: false,
      active: true,
      sortOrder: plans.length + 1,
      aiProvider: 'DEEPSEEK',
      aiModelId: 'deepseek-v4-flash',
      hasAiApiKey: false,
    });
    setFeatures(
      catalog.map((item) => ({
        featureKey: item.key,
        enabled: false,
        limitValue: item.acceptsLimit ? item.defaultLimit : null,
        label: item.label,
        description: item.description,
        acceptsLimit: item.acceptsLimit,
        defaultLimit: item.defaultLimit,
      })),
    );
    setEditOpen(true);
  };

  const openEdit = (plan) => {
    setCreating(false);
    setEditingPlan(plan);
    setAiApiKeyInput('');
    setForm({
      name: plan.name || '',
      description: plan.description || '',
      priceInput: centsToMoneyInput(plan.priceCents),
      badgeColor: plan.badgeColor || '#3b82f6',
      highlight: !!plan.highlight,
      active: !!plan.active,
      sortOrder: plan.sortOrder ?? 0,
      aiProvider: plan.aiProvider || 'DEEPSEEK',
      aiModelId: plan.aiModelId || 'deepseek-v4-flash',
      hasAiApiKey: !!plan.hasAiApiKey,
    });
    setFeatures(featureMapFromPlan(plan, catalog));
    setEditOpen(true);
  };

  const enabledCount = useMemo(
    () => features.filter((f) => f.enabled).length,
    [features],
  );

  const planHasAi = features.some(
    (f) =>
      f.enabled &&
      (f.featureKey === 'ai_daily_report' || f.featureKey === 'ai_advanced_chat'),
  );

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        priceCents: moneyToCents(form.priceInput),
        badgeColor: form.badgeColor || null,
        highlight: !!form.highlight,
        active: !!form.active,
        sortOrder: Number(form.sortOrder) || 0,
        aiProvider: form.aiProvider || 'DEEPSEEK',
        aiModelId: form.aiModelId || 'deepseek-v4-flash',
      };
      if (aiApiKeyInput.trim()) {
        payload.aiApiKey = aiApiKeyInput.trim();
      }

      const featurePayload = features.map((f) => ({
        featureKey: f.featureKey,
        enabled: !!f.enabled,
        limitValue:
          f.enabled && f.acceptsLimit
            ? Number(f.limitValue ?? f.defaultLimit ?? 0)
            : null,
      }));

      if (creating) {
        await SuperAdminService.createPlan({ ...payload, features: featurePayload });
        notify('tr', 'success', 'Plano criado', 'icon-check-2');
      } else {
        await SuperAdminService.updatePlan(editingPlan.id, payload);
        await SuperAdminService.updatePlanFeatures(editingPlan.id, featurePayload);
        notify('tr', 'success', 'Plano atualizado', 'icon-check-2');
      }
      setEditOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.message;
      notify(
        'tr',
        'danger',
        Array.isArray(msg) ? msg.join(', ') : msg || 'Erro ao salvar plano',
        'icon-alert-circle-exc',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="content">
      <style>{`
        .super-admin-plan-checks {
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
          padding-top: 8px;
        }
      `}</style>
      <Row className="mb-4 align-items-center">
        <Col lg="6">
          <h2 className="mb-1">Planos</h2>
          <p className="text-muted mb-0">
            Cadastre planos, valores e features disponíveis para cada conta
          </p>
        </Col>
        <Col lg="6" className="text-lg-right mt-3 mt-lg-0">
          <Button color="secondary" outline className="mr-2" onClick={() => navigate('/superadmin')}>
            Contas
          </Button>
          <Button color="primary" onClick={openCreate} disabled={!catalog.length}>
            Novo plano
          </Button>
        </Col>
      </Row>

      <Card>
        <CardHeader>
          <h4 className="mb-0">Catálogo de planos</h4>
        </CardHeader>
        <CardBody>
          {loading ? (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          ) : (
            <Table responsive className="mb-0">
              <thead>
                <tr>
                  <th>Plano</th>
                  <th>Preço</th>
                  <th>Features</th>
                  <th>Contas</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <span
                          style={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: plan.badgeColor || '#94a3b8',
                            display: 'inline-block',
                            marginRight: 8,
                          }}
                        />
                        <div>
                          <strong>{plan.name}</strong>
                          {plan.highlight && (
                            <Badge color="warning" pill className="ml-2">
                              Destaque
                            </Badge>
                          )}
                          {plan.description && (
                            <div className="text-muted small">{plan.description}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>{formatPlanPrice(plan.priceCents, plan.currency)}</td>
                    <td>
                      {(plan.features || []).filter((f) => f.enabled).length} ativas
                    </td>
                    <td>{plan._count?.accounts ?? 0}</td>
                    <td>
                      <Badge color={plan.active ? 'success' : 'secondary'} pill>
                        {plan.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="text-right">
                      <Button size="sm" color="primary" outline onClick={() => openEdit(plan)}>
                        Editar
                      </Button>
                    </td>
                  </tr>
                ))}
                {!plans.length && (
                  <tr>
                    <td colSpan={6} className="text-center text-muted py-4">
                      Nenhum plano cadastrado
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </CardBody>
      </Card>

      <Modal
        isOpen={editOpen}
        toggle={() => setEditOpen(false)}
        className="super-admin-dialog"
      >
        <ModalHeader toggle={() => setEditOpen(false)}>
          {creating ? 'Novo plano' : `Editar ${editingPlan?.name || 'plano'}`}
        </ModalHeader>
        <ModalBody>
          <Form id="super-admin-plan-form" onSubmit={handleSave}>
            <Row>
              <Col md="8">
                <FormGroup>
                  <Label>Nome</Label>
                  <Input
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="PedeGo Pro"
                  />
                </FormGroup>
              </Col>
              <Col md="4">
                <FormGroup>
                  <Label>Preço mensal (R$)</Label>
                  <Input
                    required
                    value={form.priceInput}
                    onChange={(e) => setForm({ ...form, priceInput: e.target.value })}
                    placeholder="149,90"
                  />
                </FormGroup>
              </Col>
              <Col md="12">
                <FormGroup>
                  <Label>Descrição</Label>
                  <Input
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Para operações pequenas."
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Cor do badge</Label>
                  <Input
                    type="color"
                    value={form.badgeColor || '#3b82f6'}
                    onChange={(e) => setForm({ ...form, badgeColor: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md="3">
                <FormGroup>
                  <Label>Ordem</Label>
                  <Input
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <Label className="d-block">Opções</Label>
                <div className="super-admin-plan-checks">
                  <FormGroup check className="mb-0">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={!!form.highlight}
                        onChange={(e) => setForm({ ...form, highlight: e.target.checked })}
                      />{' '}
                      Destaque
                    </Label>
                  </FormGroup>
                  <FormGroup check className="mb-0">
                    <Label check>
                      <Input
                        type="checkbox"
                        checked={!!form.active}
                        onChange={(e) => setForm({ ...form, active: e.target.checked })}
                      />{' '}
                      Ativo
                    </Label>
                  </FormGroup>
                </div>
              </Col>
            </Row>

            {planHasAi && (
              <>
                <hr />
                <h5 className="mb-3">Miranda AI do plano</h5>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Provedor</Label>
                      <Input
                        type="select"
                        value={form.aiProvider || 'DEEPSEEK'}
                        onChange={(e) => {
                          const provider = e.target.value;
                          const first = aiModels.find((m) => m.provider === provider);
                          setForm({
                            ...form,
                            aiProvider: provider,
                            aiModelId: first?.modelId || form.aiModelId,
                          });
                        }}
                      >
                        <option value="DEEPSEEK">DeepSeek</option>
                        <option value="OPENAI">OpenAI (GPT)</option>
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Modelo</Label>
                      <Input
                        type="select"
                        value={form.aiModelId || ''}
                        onChange={(e) => setForm({ ...form, aiModelId: e.target.value })}
                      >
                        {aiModels
                          .filter((m) => m.provider === form.aiProvider)
                          .map((m) => (
                            <option key={m.id} value={m.modelId}>
                              {m.label}
                            </option>
                          ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="12">
                    <FormGroup>
                      <Label>
                        API Key do plano{' '}
                        {form.hasAiApiKey && (
                          <Badge color="success" pill className="ml-1">
                            configurada
                          </Badge>
                        )}
                      </Label>
                      <Input
                        type="password"
                        autoComplete="new-password"
                        value={aiApiKeyInput}
                        onChange={(e) => setAiApiKeyInput(e.target.value)}
                        placeholder={
                          form.hasAiApiKey
                            ? 'Deixe em branco para manter a atual'
                            : 'Usada por todas as contas deste plano'
                        }
                      />
                      <small className="text-muted">
                        Ao salvar, as contas deste plano sincronizam provedor, modelo e key.
                      </small>
                    </FormGroup>
                  </Col>
                </Row>
              </>
            )}

            <hr />
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">Features do plano</h5>
              <Badge color="info" pill>
                {enabledCount} ativas
              </Badge>
            </div>

            {features.map((feat, idx) => (
              <div
                key={feat.featureKey}
                className="mb-3 p-3"
                style={{
                  border: '1px solid rgba(0,0,0,0.08)',
                  borderRadius: 8,
                  background: feat.enabled ? 'rgba(59,130,246,0.08)' : 'transparent',
                }}
              >
                <div className="d-flex align-items-start">
                  <Input
                    type="checkbox"
                    className="mt-1 mr-2"
                    checked={!!feat.enabled}
                    onChange={(e) => {
                      const next = [...features];
                      next[idx] = {
                        ...feat,
                        enabled: e.target.checked,
                        limitValue:
                          e.target.checked && feat.acceptsLimit
                            ? feat.limitValue ?? feat.defaultLimit
                            : feat.limitValue,
                      };
                      setFeatures(next);
                    }}
                  />
                  <div className="flex-grow-1">
                    <div style={{ fontWeight: 600 }}>{feat.label}</div>
                    <small className="text-muted">{feat.description}</small>
                    {feat.enabled && feat.acceptsLimit && (
                      <FormGroup className="mb-0 mt-2" style={{ maxWidth: 220 }}>
                        <Label className="small mb-1">
                          {feat.featureKey === 'ai_advanced_chat'
                            ? 'Limite de requests / mês'
                            : feat.featureKey === 'extra_users'
                              ? 'Limite de usuários'
                              : feat.featureKey === 'daily_order_limit'
                                ? 'Limite de pedidos / dia'
                                : 'Limite'}
                        </Label>
                        <Input
                          type="number"
                          min={0}
                          value={feat.limitValue ?? feat.defaultLimit ?? 0}
                          onChange={(e) => {
                            const next = [...features];
                            next[idx] = {
                              ...feat,
                              limitValue: Number(e.target.value),
                            };
                            setFeatures(next);
                          }}
                        />
                      </FormGroup>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </Form>
        </ModalBody>
        <ModalFooter className="super-admin-modal-footer">
          <Button color="secondary" outline type="button" onClick={() => setEditOpen(false)}>
            Cancelar
          </Button>
          <Button
            color="primary"
            type="submit"
            form="super-admin-plan-form"
            disabled={saving}
          >
            {saving ? 'Salvando...' : creating ? 'Criar plano' : 'Salvar'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
}

export default SuperAdminPlansPage;
